/* eslint-disable sonarjs/cognitive-complexity */
// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

import useRouter from '../../hooks/useRouter';
import Formatter from '../../i18n/formatter';
import { getCurrency, __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { BasketStatuses } from '../../types/basket';
import { Licences } from '../../types/common';
import { BetslipFilters } from '../../types/history';
import { showWebsiteLoginForm } from '../../utils';
import BasketAlert from '../BasketAlert';
import Link from '../Link';
import Primary from '../PrimaryButton';

const loaderAnim = keyframes`
  0% {
    transform: rotate(0deg);
  }  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  border-radius: 50%;
  margin: 0 auto;
  position: relative;
  text-indent: -9999em;
  border-top: 0.2em solid rgba(255, 255, 255, 0.2);
  border-right: 0.2em solid rgba(255, 255, 255, 0.2);
  border-bottom: 0.2em solid rgba(255, 255, 255, 0.2);
  border-left: 0.2em solid #ffffff;
  transform: translateZ(0);
  animation: ${loaderAnim} 1s infinite linear;
`;

const Wrap = styled.div`
  padding: ${({ $paddingTop }) => ($paddingTop ? '1.2em 0.6em 0.6em' : '0.6em')};
`;

const Line = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const commonButton = css`
  font-size: 1em;
  width: 100%;
  min-height: 2.6em;
  color: #fff;
  ${({ isDisabled }) =>
    isDisabled
      ? css`
          opacity: 0.5;
          pointer-events: none;
        `
      : ''}
  &:nth-of-type(n+2) {
    margin-left: 0.4em;
  }
`;

const PrimaryButton = styled(Primary)`
  ${commonButton}
  && {
    color: ${theme.BasketCtas.primary.color};
    background-color: ${theme.BasketCtas.primary.backgroundColor};
  }
`;

const SecondaryButton = styled(Primary)`
  ${commonButton}
  ${({ $isBigger }) =>
    $isBigger
      ? css`
          width: 150%;
          flex-grow: 2;
        `
      : ''}
  && {
    color: ${theme.BasketCtas.secondary.color};
    background-color: ${theme.BasketCtas.secondary.backgroundColor};
  }
`;

const TertiaryButton = styled(Primary)`
  ${commonButton}
  && {
    color: ${theme.BasketCtas.tertiary.color};
    background-color: ${theme.BasketCtas.tertiary.backgroundColor};
  }
`;

const CashoutButton = styled(Primary)`
  ${commonButton}
  && {
    color: ${theme.BasketCtas.cashout.color};
    background-color: ${theme.BasketCtas.cashout.backgroundColor};
  }
`;

const StyledAlert = styled(BasketAlert)`
  margin: 0.4em 0;
`;

const BetAmount = styled.span`
  font-size: 1.2em;
`;

const Loading = styled(LoadingSpinner)`
  width: 1.6em;
  height: 1.6em;
`;

const BiggerLabel = styled.span`
  font-size: 1.2em;
`;

const handleShowLoginForm = () => {
  showWebsiteLoginForm();
};

type BasketCtasProps = {
  showResume: boolean;
  nbItemInError: number;
  nbItemsMemoized: number;
  connected: boolean;
  isMobile: boolean;
  showAcceptChangebutton: boolean;
  totalCost: number;
  isCashout: boolean;
  isButtonDisable: boolean;
  hasSummaryPresets: boolean;
  isValidating: boolean;
  isEmpty: boolean;
  cashoutValue: number | null;
  basketClear: (...params: unknown) => unknown;
  basketClearValidatedAndWaiting: (...params: unknown) => unknown;
  basketGetSelection: (...params: unknown) => unknown;
  basketAcceptChanges: (...params: unknown) => unknown;
  basketSubmit: (...params: unknown) => unknown;
  licence: string;
};

const BasketCtas = ({
  showResume,
  nbItemInError,
  nbItemsMemoized,
  connected,
  isMobile,
  isCashout,
  totalCost,
  showAcceptChangebutton,
  isButtonDisable,
  hasSummaryPresets,
  isValidating,
  cashoutValue,
  isEmpty,
  basketClear,
  basketClearValidatedAndWaiting,
  basketGetSelection,
  basketAcceptChanges,
  basketSubmit,
  licence,
}: BasketCtasProps) => {
  const { history, location } = useRouter();

  const cashoutValueLabel = useMemo(() => Formatter.formatNumber(+cashoutValue, getCurrency()), [cashoutValue]);

  const handleClearAndContinue = useCallback(() => {
    if (location.hash?.indexOf('cart') !== -1) {
      history.replace({ hash: '' });
    }
    basketClear();
  }, [location, history, basketClear]);

  const renderAcceptOrClose = useCallback(
    () => (
      <Line>
        {nbItemsMemoized > 0 ? (
          <SecondaryButton $isBigger onClick={basketGetSelection}>
            {__('Save my selection', nbItemsMemoized, 'Save my selections')}
          </SecondaryButton>
        ) : null}
        <PrimaryButton onClick={handleClearAndContinue}>{__('Close')}</PrimaryButton>
      </Line>
    ),
    [nbItemsMemoized, basketGetSelection, handleClearAndContinue],
  );

  const renderResume = useCallback(() => {
    return (
      <>
        {nbItemInError > 0 ? (
          <Wrap>
            {renderAcceptOrClose()}
            <StyledAlert
              message={__('%1$s bet could not be validated', nbItemInError, '%1$s bets could not be validated')}
              status={BasketStatuses.ERROR}
            />
            <TertiaryButton onClick={basketClearValidatedAndWaiting}>
              {__('!action:viewMyNonValidatedBets')}
            </TertiaryButton>
          </Wrap>
        ) : null}
        {nbItemInError === 0 ? <Wrap>{renderAcceptOrClose()}</Wrap> : null}
      </>
    );
  }, [nbItemInError, basketClearValidatedAndWaiting, renderAcceptOrClose]);

  const renderContent = useCallback(() => {
    if (isEmpty && connected && cashoutValue && licence !== Licences.DE) {
      return (
        <Wrap>
          <Link to={`/history/${BetslipFilters.RUNNING}`}>
            <CashoutButton>
              {__('Cashout: %2$s available', [+cashoutValue, cashoutValueLabel], 'Cashout: %2$s available')}
            </CashoutButton>
          </Link>
        </Wrap>
      );
    }
    return !isEmpty ? (
      <Wrap $paddingTop={hasSummaryPresets}>
        {/* Accept changes */}
        {showAcceptChangebutton ? (
          <SecondaryButton onClick={basketAcceptChanges}>{__('Accept changes')}</SecondaryButton>
        ) : null}
        {/* Bet */}
        {!showAcceptChangebutton ? (
          <>
            {connected ? (
              <PrimaryButton
                isDisabled={isButtonDisable}
                onClick={!isValidating ? basketSubmit : () => {}}
                data-testid="basket-submit-button"
              >
                <BiggerLabel>
                  {isValidating ? (
                    <Loading />
                  ) : (
                    <>
                      {isCashout ? (
                        __('Confirm')
                      ) : (
                        <>
                          {__('!action:Bet')}
                          &nbsp;
                          <BetAmount>
                            {totalCost > 0 ? Formatter.formatNumber(totalCost, getCurrency()) : null}
                          </BetAmount>
                        </>
                      )}
                    </>
                  )}
                </BiggerLabel>
              </PrimaryButton>
            ) : null}
            {/* Not connected mobile */}
            {!connected && isMobile ? (
              <Link
                to={{
                  hash: '#login',
                  state: { modal: true },
                }}
              >
                <PrimaryButton disabled={showAcceptChangebutton || totalCost <= 0}>
                  {__('Sign-in and place bet')}
                </PrimaryButton>
              </Link>
            ) : null}
            {/* Not connected desktop */}
            {!connected && !isMobile ? (
              <PrimaryButton disabled={showAcceptChangebutton || totalCost <= 0} onClick={handleShowLoginForm}>
                {__('Sign-in and place bet')}
              </PrimaryButton>
            ) : null}
          </>
        ) : null}
      </Wrap>
    ) : null;
  }, [
    isEmpty,
    connected,
    cashoutValue,
    licence,
    hasSummaryPresets,
    showAcceptChangebutton,
    basketAcceptChanges,
    isButtonDisable,
    isValidating,
    basketSubmit,
    isCashout,
    totalCost,
    isMobile,
    cashoutValueLabel,
  ]);

  return showResume ? renderResume() : renderContent();
};

export default BasketCtas;
